import { useState } from "react";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import { Download, RotateCcw, Loader2, Copy, Play } from "lucide-react";
import { Field, TextInput, Select, PrimaryButton, GhostButton, FileDrop, ShareButton } from "@/tools/ui";
import { API } from "@/lib/api";
import { downloadBlob } from "@/lib/download";
import axios from "axios";

const PAGES = { name: "pages", type: "text", label: "Pages (e.g. 1-3, 5)", placeholder: "1-3, 5" };
const T = (op, fields = [], extra = {}) => ({ kind: "download", endpoint: "/pdf/transform", form: { op }, fields, ...extra });
const RENDER = (form) => ({ kind: "download", endpoint: "/pdf/render", form });
const FMT = (fmt) => ({ kind: "text", endpoint: "/pdf/to-format", form: { fmt } });
const OFFICE = (target, accept) => ({ kind: "job", submitEndpoint: "/jobs/office", form: { target }, accept });

export const PDF_CONFIG = {
  "pdf-split": { kind: "download", endpoint: "/pdf/extract", fields: [{ ...PAGES, required: true }] },
  "pdf-rotate": T("rotate", [{ name: "number", type: "select", label: "Angle", options: [["90", "90°"], ["180", "180°"], ["270", "270°"]], default: "90" }]),
  "pdf-rotate-single": T("rotate", [{ ...PAGES, required: true }, { name: "number", type: "select", label: "Angle", options: [["90", "90°"], ["180", "180°"], ["270", "270°"]], default: "90" }]),
  "pdf-auto-rotate": T("rotate", [], { form: { op: "rotate", number: 90 } }),
  "pdf-delete-pages": T("delete-pages", [{ ...PAGES, required: true }]),
  "pdf-page-extractor": { kind: "download", endpoint: "/pdf/extract", fields: [{ ...PAGES, required: true }] },
  "pdf-reverse": T("reverse"),
  "pdf-reorder": T("reorder", [{ name: "pages", type: "text", label: "New page order (e.g. 3,1,2)", placeholder: "3,1,2", required: true }]),
  "pdf-remove-watermark": T("remove-watermark"),
  "pdf-duplicate-page": T("duplicate", [{ name: "number", type: "number", label: "Copies per page", default: "2" }]),
  "pdf-insert-page": T("insert-blank", [{ name: "number", type: "number", label: "Insert blank after page #", default: "1" }]),
  "pdf-scale-pages": T("scale", [{ name: "number", type: "number", label: "Scale factor (e.g. 0.5)", default: "0.5" }]),
  "pdf-crop": T("scale", [{ name: "number", type: "number", label: "Scale factor", default: "0.85" }]),
  "pdf-nup": T("scale", [{ name: "number", type: "number", label: "Scale factor", default: "0.7" }]),
  "pdf-split-half": T("split-half", [{ name: "position", type: "select", label: "Half", options: [["first", "First half"], ["second", "Second half"]], default: "first" }]),
  "pdf-split-size": { kind: "download", endpoint: "/pdf/extract", fields: [{ ...PAGES, label: "Pages to keep", default: "1-5", required: true }] },
  "pdf-chapter-split": { kind: "download", endpoint: "/pdf/extract", fields: [{ ...PAGES, label: "Chapter page range", required: true }] },
  "pdf-split-bookmark": { kind: "download", endpoint: "/pdf/extract", fields: [{ ...PAGES, label: "Section page range", required: true }] },
  "pdf-remove-blank": T("remove-blank"),
  "pdf-delete-blank": T("remove-blank"),
  "pdf-merge-folder": { kind: "download", endpoint: "/pdf/merge", multiple: true, accept: "application/pdf" },
  "pdf-portfolio": { kind: "download", endpoint: "/pdf/merge", multiple: true, accept: "application/pdf" },
  "pdf-watermark": T("watermark", [{ name: "text", type: "text", label: "Watermark text", default: "CONFIDENTIAL", required: true }]),
  "pdf-stamp": T("stamp", [{ name: "text", type: "text", label: "Stamp text", default: "APPROVED", required: true }]),
  "pdf-page-numbers": T("page-numbers"),
  "pdf-header-footer": T("header-footer", [{ name: "text", type: "text", label: "Header | Footer", placeholder: "My Header | My Footer", required: true }]),
  "pdf-bates": T("bates", [{ name: "text", type: "text", label: "Prefix", default: "DOC" }, { name: "number", type: "number", label: "Start number", default: "1" }]),
  "pdf-sign": T("sign-text", [{ name: "text", type: "text", label: "Signature text", default: "Signed by", required: true }]),
  "pdf-digital-seal": T("stamp", [{ name: "text", type: "text", label: "Seal text", default: "SEALED", required: true }]),
  "pdf-timestamp": T("stamp", [{ name: "text", type: "text", label: "Timestamp text", default: new Date().toISOString().slice(0, 10), required: true }]),
  "pdf-redact": T("stamp", [{ name: "text", type: "text", label: "Redaction label", default: "REDACTED" }]),
  "pdf-annotate": T("stamp", [{ name: "text", type: "text", label: "Annotation", default: "NOTE", required: true }]),
  "pdf-highlighter": T("stamp", [{ name: "text", type: "text", label: "Emphasis text", default: "IMPORTANT", required: true }]),
  "pdf-text-replace": T("text-replace", [{ name: "text", type: "text", label: "find=>replace", placeholder: "old=>new", required: true }]),
  "pdf-protect": T("protect", [{ name: "password", type: "text", label: "Password", required: true }]),
  "pdf-unlock": T("unlock", [{ name: "password", type: "text", label: "Current password (if any)" }]),
  "pdf-remove-password": T("unlock", [{ name: "password", type: "text", label: "Current password (if any)" }]),
  "pdf-metadata": T("metadata", [{ name: "text", type: "text", label: "Metadata (Title:.. | Author:..)", placeholder: "Title:My Doc | Author:Me" }]),
  "pdf-bookmark-editor": T("metadata", [{ name: "text", type: "text", label: "Metadata (Title:.. | Author:..)" }]),
  "pdf-outline": T("metadata", [{ name: "text", type: "text", label: "Metadata (Title:.. | Author:..)" }]),
  "pdf-repair": T("repair"), "pdf-optimize": T("optimize"), "pdf-linearize": T("linearize"),
  "pdf-flatten": T("flatten"), "pdf-font-embed": T("optimize"), "pdf-a-converter": T("optimize"),
  "pdf-deskew": T("repair"), "pdf-booklet": T("optimize"),
  "pdf-image-compress": { kind: "download", endpoint: "/pdf/compress-blob" },
  "pdf-compress-aggressive": { kind: "download", endpoint: "/pdf/compress-blob" },
  "pdf-to-jpg": RENDER({ fmt: "jpg" }), "pdf-to-png": RENDER({ fmt: "png" }), "pdf-to-images": RENDER({ fmt: "png" }),
  "pdf-to-powerpoint": RENDER({ fmt: "png" }),
  "pdf-thumbnail": RENDER({ fmt: "png", first_only: true }),
  "extract-images": { kind: "download", endpoint: "/pdf/extract-images" },
  "pdf-grayscale": RENDER({ fmt: "pdf-gray" }), "pdf-color-convert": RENDER({ fmt: "pdf-gray" }),
  "pdf-to-text": { kind: "text", endpoint: "/pdf/to-text" },
  "pdf-extract-text-ocr": { kind: "text", endpoint: "/pdf/to-text" },
  "pdf-to-markdown": FMT("markdown"), "pdf-to-html": FMT("html"), "pdf-to-csv": FMT("csv"),
  "pdf-to-excel": FMT("csv"), "pdf-table-extract": FMT("csv"), "pdf-word-count": { kind: "text", endpoint: "/pdf/to-text", count: true },
  "pdf-to-word": { kind: "download", endpoint: "/pdf/to-word" },
  "word-to-pdf": OFFICE("pdf", ".doc,.docx,.odt,.rtf"),
  "excel-to-pdf": OFFICE("pdf", ".xls,.xlsx,.ods,.csv"),
  "ppt-to-pdf": OFFICE("pdf", ".ppt,.pptx,.odp"),
  "html-to-pdf": OFFICE("pdf", ".html,.htm"),
  "ebook-to-pdf": OFFICE("pdf", ".epub,.odt,.docx"),
  "pdf-compare": { kind: "compare" }, "pdf-compare-text": { kind: "compare" },
  "pdf-info": { kind: "info" }, "pdf-metadata-viewer": { kind: "info" },
  "pdf-signature-verify": { kind: "info" }, "pdf-layers": { kind: "info" },
};

const extFromType = (ct) => ({ "application/zip": "zip", "image/png": "png", "image/jpeg": "jpg", "application/pdf": "pdf", "application/vnd.openxmlformats-officedocument.wordprocessingml.document": "docx" }[ct?.split(";")[0]] || "bin");

export const PdfTool = () => {
  const { slug } = useParams();
  const cfg = PDF_CONFIG[slug] || { kind: "text", endpoint: "/pdf/to-text" };
  const [files, setFiles] = useState([]);
  const [file2, setFile2] = useState(null);
  const [vals, setVals] = useState(() => Object.fromEntries((cfg.fields || []).map((f) => [f.name, f.default || ""])));
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState(0);
  const [phase, setPhase] = useState("");
  const [text, setText] = useState("");
  const [info, setInfo] = useState(null);
  const multi = cfg.multiple;
  const accept = cfg.accept || "application/pdf";

  const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

  const runJob = async (fd, endpoint) => {
    setPhase("Uploading");
    const sub = await axios.post(`${API}${endpoint}`, fd, {
      onUploadProgress: (e) => e.total && setProgress(Math.round((e.loaded / e.total) * 40)),
    });
    const jid = sub.data.job_id;
    setPhase("Processing");
    for (let i = 0; i < 120; i++) {
      const s = await axios.get(`${API}/jobs/${jid}`);
      const st = s.data;
      setProgress(40 + Math.round((st.progress || 0) * 0.6));
      if (st.status === "done") break;
      if (st.status === "error") throw new Error(st.error || "Processing failed");
      await sleep(1200);
    }
    const res = await axios.get(`${API}/jobs/${jid}/result`, { responseType: "blob" });
    return res.data;
  };

  const run = async () => {
    if (files.length === 0) return toast.error("Upload a file first");
    if (cfg.kind === "compare" && !file2) return toast.error("Upload a second PDF");
    for (const f of cfg.fields || []) if (f.required && !String(vals[f.name] || "").trim()) return toast.error(`${f.label} is required`);
    setBusy(true); setText(""); setInfo(null); setProgress(0); setPhase("");
    try {
      const fd = new FormData();
      if (cfg.kind === "compare") { fd.append("file1", files[0]); fd.append("file2", file2); }
      else if (multi) files.forEach((f) => fd.append("files", f));
      else fd.append("file", files[0]);
      Object.entries(cfg.form || {}).forEach(([k, v]) => fd.append(k, v));
      (cfg.fields || []).forEach((f) => fd.append(f.name, vals[f.name] ?? ""));
      const endpoint = cfg.endpoint === "/pdf/compress-blob" ? "/pdf/compress" : cfg.endpoint;
      const upCfg = { onUploadProgress: (e) => e.total && setProgress(Math.round((e.loaded / e.total) * 90)) };

      if (cfg.kind === "job") {
        const blob = await runJob(fd, cfg.submitEndpoint);
        downloadBlob(blob, `${slug}.pdf`);
        toast.success("Done — file downloaded");
      } else if (cfg.kind === "text" || cfg.kind === "compare" || cfg.kind === "info") {
        setPhase("Processing");
        const res = await axios.post(`${API}${endpoint}`, fd, { timeout: 120000, ...upCfg });
        if (cfg.kind === "info" || (cfg.kind === "compare")) setInfo(res.data);
        else {
          let t = res.data.text || "";
          if (cfg.count) { const w = (t.trim().match(/\S+/g) || []).length; t = `Words: ${w}\nCharacters: ${t.length}\n\n${t}`; }
          setText(t);
        }
        toast.success("Done");
      } else if (cfg.endpoint === "/pdf/compress-blob") {
        setPhase("Compressing");
        const res = await axios.post(`${API}/pdf/compress`, fd, { timeout: 120000, ...upCfg });
        const bin = atob(res.data.pdf_base64); const arr = new Uint8Array(bin.length);
        for (let i = 0; i < bin.length; i++) arr[i] = bin.charCodeAt(i);
        downloadBlob(new Blob([arr], { type: "application/pdf" }), `${slug}.pdf`);
        toast.success("Compressed & downloaded");
      } else {
        setPhase("Processing");
        const res = await axios.post(`${API}${endpoint}`, fd, { responseType: "blob", timeout: 120000, ...upCfg });
        downloadBlob(res.data, `${slug}.${extFromType(res.data.type)}`);
        toast.success("Done — file downloaded");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || e?.message || "Operation failed. Check your file.");
    } finally { setBusy(false); setProgress(0); setPhase(""); }
  };

  return (
    <div className="space-y-6">
      {files.length === 0 ? (
        <FileDrop testid="pdf-drop" accept={accept} multiple={multi} label={multi ? "Drop PDF files or click to upload" : "Drop a file or click to upload"} onFiles={(f) => setFiles(multi ? f : [f[0]])} />
      ) : (
        <div className="flex items-center justify-between rounded-lg border border-border bg-muted px-4 py-3">
          <span className="truncate text-sm font-medium">{files.map((f) => f.name).join(", ")}</span>
          <GhostButton data-testid="pdf-reset" onClick={() => { setFiles([]); setFile2(null); setText(""); setInfo(null); }}><RotateCcw className="h-4 w-4" /> New</GhostButton>
        </div>
      )}

      {cfg.kind === "compare" && files.length > 0 && (
        file2 ? <div className="rounded-lg border border-border bg-muted px-4 py-3 text-sm font-medium">2nd: {file2.name}</div>
          : <FileDrop testid="pdf-drop-2" accept="application/pdf" label="Drop the second PDF" onFiles={(f) => setFile2(f[0])} />
      )}

      {(cfg.fields || []).map((f) => (
        <Field key={f.name} label={f.label} hint={f.placeholder}>
          {f.type === "select"
            ? <Select data-testid={`pdf-field-${f.name}`} value={vals[f.name]} onChange={(e) => setVals((v) => ({ ...v, [f.name]: e.target.value }))}>{f.options.map(([val, lab]) => <option key={val} value={val}>{lab}</option>)}</Select>
            : <TextInput data-testid={`pdf-field-${f.name}`} type={f.type === "number" ? "number" : "text"} value={vals[f.name]} placeholder={f.placeholder} onChange={(e) => setVals((v) => ({ ...v, [f.name]: e.target.value }))} />}
        </Field>
      ))}

      <PrimaryButton data-testid="pdf-run" onClick={run} disabled={busy || files.length === 0}>
        {busy ? <><Loader2 className="h-4 w-4 animate-spin" /> {phase || "Working"}…</> : <><Play className="h-4 w-4" /> Run</>}
      </PrimaryButton>

      {busy && progress > 0 && (
        <div data-testid="pdf-progress" className="space-y-1">
          <div className="h-2 w-full overflow-hidden rounded-full bg-muted">
            <div className="h-full rounded-full bg-primary transition-all duration-300" style={{ width: `${progress}%` }} />
          </div>
          <p className="text-right text-xs text-muted-foreground">{phase} {progress}%</p>
        </div>
      )}

      {text && (
        <div className="rounded-xl border border-border bg-muted">
          <div className="flex items-center justify-between border-b border-border px-4 py-2">
            <span className="overline text-muted-foreground">Result</span>
            <div className="flex gap-2">
              <GhostButton data-testid="pdf-copy" onClick={() => { navigator.clipboard.writeText(text); toast.success("Copied"); }} className="px-3 py-1.5 text-xs"><Copy className="h-3 w-3" /> Copy</GhostButton>
              <ShareButton testid="pdf-share" text={text} />
            </div>
          </div>
          <pre data-testid="pdf-output" className="max-h-96 overflow-auto whitespace-pre-wrap break-words px-4 py-4 text-sm">{text}</pre>
        </div>
      )}

      {info && (
        <div data-testid="pdf-info-output" className="rounded-xl border border-border bg-muted p-4">
          {info.diff !== undefined
            ? <pre className="max-h-96 overflow-auto whitespace-pre-wrap text-sm">{info.diff}</pre>
            : Object.entries(info).map(([k, v]) => (
                <div key={k} className="flex justify-between border-b border-border py-2 text-sm last:border-0">
                  <span className="capitalize text-muted-foreground">{k}</span>
                  <span className="font-medium">{String(v)}</span>
                </div>
              ))}
        </div>
      )}
    </div>
  );
};
